import ArrowClockwiseRegular from "owa-fluent-icons-svg/lib/icons/ArrowClockwiseRegular";
import { getListViewNotificationDimensions } from "owa-mail-layout";
import { lazyShowNotification, lazyDismissNotification } from "owa-notification-bar";
import { getUserConfiguration } from "owa-session-store";
import getPluralizedString from "owa-plural/lib/getPluralizedString";
import { postSyncNewMessageSingular, postSyncNewConversationSingular, postSyncNewMessagefirstPlural, postSyncNewConversationfirstPlural, postSyncNewMessageSecondPlural, postSyncNewConversationSecondPlural, } from "./showPostSyncNotification.locstring.json";
import type { MailListRowDataType, TableView } from "owa-mail-list-store";
import { MailRowDataPropertyGetter } from "owa-mail-list-store";

/**
 * Calculates the count of new row keys for notification purposes
 * @param tableView - The table view containing existing rows
 * @param rows - The new rows being processed
 * @param showNotification - Whether to show notification
 * @returns The count of new row keys, or -1 if notification should not be shown
 */
function calculateNewRowKeysCount(tableView: TableView, rows: MailListRowDataType[]): number {
    if (tableView.rowKeys.length <= 0 || rows.length <= 0) {
        return -1;
    }
    
    const firstUnpinnedRowKeyInTableIndex = tableView.rowKeys.findIndex(rowKey => !MailRowDataPropertyGetter.getIsPinned(rowKey, tableView));
    
    if (firstUnpinnedRowKeyInTableIndex === -1) {
        return -1;
    }
    
    const firstUnpinnedRowKeyInTable = tableView.rowKeys[firstUnpinnedRowKeyInTableIndex];
    const firstUnpinnedRowKey = rows.findIndex(row => row.InstanceKey === firstUnpinnedRowKeyInTable);
    
    if (firstUnpinnedRowKey === -1) {
        return -1;
    }
    
    return firstUnpinnedRowKey - firstUnpinnedRowKeyInTableIndex;
}

/**
 * Show notification after mailbox sync
 * This is used to notify the user that the mailbox has been synced successfully.
 * Calls lazyShowNotification directly instead of going through showTriageActionNotification.
 */
export default function showPostSyncNotification(tableView: TableView, rows: MailListRowDataType[], isConversation: boolean): void {
    if (!getUserConfiguration().UserOptions?.ShowNotificationBar) {
        // Only show notification based on user setting for the Global account and if there are new rows added
        return;
    }
    
    const newRowsAdded = calculateNewRowKeysCount(tableView, rows);
    
    if (newRowsAdded <= 0) {
        // If there are no new rows added, do not show notification
        return;
    }
    
    // Dismiss the previous notification if it is already showing.
    lazyDismissNotification.importAndExecute("postSyncMailboxNotification");
    
    const pluralizedString = isConversation
        ? getPluralizedString(newRowsAdded, "", postSyncNewConversationSingular, postSyncNewConversationfirstPlural, postSyncNewConversationSecondPlural)
        : getPluralizedString(newRowsAdded, "", postSyncNewMessageSingular, postSyncNewMessagefirstPlural, postSyncNewMessageSecondPlural);
    
    const notificationDimensions = getListViewNotificationDimensions();
    
    lazyShowNotification.importAndExecute("postSyncMailboxNotification", "MailModuleNotificationBarHost", window, pluralizedString, {
        icon: ArrowClockwiseRegular,
        allowClear: true,
        // Want 12 px of padding on either side of the notification bar
        minWidth: notificationDimensions.minWidth,
        maxWidth: notificationDimensions.maxWidth,
        allowAutoDismiss: true,
    }, undefined /* renderContent */, true /* useV9 */);
}
